<template>
  <div class="mnt-metrics">
    <!-- Tarjeta 1: Tickets abiertos -->
    <button class="mnt-metric-card" :class="`mnt-metric-card--${openTickets.color}`" @click="$emit('filter-open')">
      <span v-if="openTickets.hasUnassignedOver2h" class="mnt-metric-card__blink mnt-metric-card__blink--normal"></span>
      <span class="mnt-metric-card__label">Tickets abiertos</span>
      <span class="mnt-metric-card__value" :class="`mnt-metric-card__value--${openTickets.color}`">{{ openTickets.total }}</span>
      <span class="mnt-metric-card__sub">
        <span :class="openTickets.unassignedCount > 0 ? 'mnt-metric-card__sub--red' : ''">{{ openTickets.unassignedCount }} sin asignar</span>
        · {{ openTickets.assignedCount }} asignados
      </span>
      <span class="mnt-metric-card__sub">
        <span class="mnt-metric-card__sub--red">{{ openTickets.criticalCount }} críticos</span>
        · <span class="mnt-metric-card__sub--amber">{{ openTickets.highCount }} altos</span>
      </span>
    </button>

    <!-- Tarjeta 2: Tiempo de resolución -->
    <button class="mnt-metric-card" :class="`mnt-metric-card--${resolutionTime.color}`" @click="$emit('open-resolution-analysis')">
      <span class="mnt-metric-card__label">Tiempo de resolución</span>
      <span class="mnt-metric-card__sublabel">últimos 30 días</span>
      <span class="mnt-metric-card__value">{{ resolutionTime.avgLabel }}</span>
      <span v-if="resolutionTime.trendPercent !== null" class="mnt-metric-card__sub" :class="resolutionTime.trendPercent <= 0 ? 'mnt-metric-card__sub--green' : 'mnt-metric-card__sub--red'">
        {{ resolutionTime.trendPercent <= 0 ? '▼' : '▲' }} {{ Math.abs(resolutionTime.trendPercent) }}% vs mes anterior
      </span>
      <span class="mnt-metric-card__sub mnt-metric-card__sub--muted">
        Correctivos {{ formatType(resolutionTime.byType.CORRECTIVE) }} · Preventivos {{ formatType(resolutionTime.byType.PREVENTIVE) }} · Mejoras {{ formatType(resolutionTime.byType.IMPROVEMENT) }}
      </span>
      <span v-if="resolutionTime.bestTechnician" class="mnt-metric-card__sub mnt-metric-card__sub--muted">
        Mejor: <span class="mnt-metric-card__sub--navy">{{ resolutionTime.bestTechnician.name }}</span> · {{ resolutionTime.bestTechnician.avgHours }}h promedio
      </span>
    </button>

    <!-- Tarjeta 3: Costo del mes -->
    <button class="mnt-metric-card" :class="`mnt-metric-card--${cost.color}`" @click="$emit('filter-cost')">
      <span class="mnt-metric-card__label">Costo operativo</span>
      <span class="mnt-metric-card__sublabel">{{ currentMonthLabel }}</span>
      <span class="mnt-metric-card__value mnt-metric-card__value--mono">{{ formatCurrency(cost.total) }}</span>
      <span class="mnt-metric-card__sub mnt-metric-card__sub--muted">
        Cobrado {{ formatCurrency(cost.billed) }} · Absorbido {{ formatCurrency(cost.absorbed) }}
      </span>
      <span class="mnt-metric-card__sub" :class="cost.vsAvg > 0 ? 'mnt-metric-card__sub--red' : 'mnt-metric-card__sub--green'">
        {{ cost.vsAvg > 0 ? '▲ +' : '▼ ' }}{{ formatCurrency(Math.abs(cost.vsAvg)) }} vs promedio 6m
      </span>
      <span class="mnt-metric-card__sub mnt-metric-card__sub--muted">
        Mano de obra {{ formatCurrency(cost.byCategory.LABOR) }} · Materiales {{ formatCurrency(cost.byCategory.MATERIALS) }} · Externos {{ formatCurrency(cost.byCategory.EXTERNAL) }}
      </span>
    </button>

    <!-- Tarjeta 4: SLA en riesgo --> 
    <button class="mnt-metric-card mnt-metric-card--sla-card" :class="slaRisk.count > 0 ? 'mnt-metric-card--red' : 'mnt-metric-card--gray'" @click="$emit('filter-sla-risk')">      <span v-if="slaRisk.count > 0" class="mnt-metric-card__blink mnt-metric-card__blink--fast"></span>
      <span class="mnt-metric-card__label">SLA en riesgo</span>
      <span class="mnt-metric-card__sublabel">próximas 4 horas</span>
      <span class="mnt-metric-card__value" :class="slaRisk.count > 0 ? 'mnt-metric-card__value--red' : 'mnt-metric-card__value--gray'">{{ slaRisk.count }}</span>

      <template v-if="slaRisk.count > 0">
        <span v-for="t in slaRisk.tickets.slice(0, 1)" :key="t.id" class="mnt-metric-card__sub">
          {{ t.code }} · {{ t.tenantName || t.plotName }} · <span class="mnt-metric-card__sub--red-bold">Vence en {{ formatMinutes(t.minutesRemaining) }}</span>
        </span>
        <span v-if="slaRisk.tickets[0]?.technicianName" class="mnt-metric-card__sub mnt-metric-card__sub--muted">
          Técnico: {{ slaRisk.tickets[0].technicianName }}
        </span>
        <span v-if="slaRisk.additionalIn24hCount > 0" class="mnt-metric-card__sub mnt-metric-card__sub--amber">
          {{ slaRisk.additionalIn24hCount }} tickets adicionales en riesgo en 24h
        </span>
      </template>
      <span v-else class="mnt-metric-card__sub mnt-metric-card__sub--green">
        Todos los tickets dentro de tiempo · Sin compromisos en riesgo
      </span>
    </button>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  metrics: {
    type: Object,
    default: () => ({
      openTickets: { total: 0, unassignedCount: 0, assignedCount: 0, criticalCount: 0, highCount: 0, hasUnassignedOver2h: false, color: 'blue' },
      resolutionTime: { avgLabel: '—', trendPercent: null, byType: {}, bestTechnician: null, color: 'green' },
      cost: { total: 0, billed: 0, absorbed: 0, avg6m: 0, vsAvg: 0, byCategory: { LABOR: 0, MATERIALS: 0, EXTERNAL: 0 }, color: 'green' },
      slaRisk: { count: 0, tickets: [], additionalIn24hCount: 0 },
    }),
  },
})

defineEmits(['filter-open', 'open-resolution-analysis', 'filter-cost', 'filter-sla-risk'])

const openTickets = computed(() => props.metrics.openTickets)
const resolutionTime = computed(() => props.metrics.resolutionTime)
const cost = computed(() => props.metrics.cost)
const slaRisk = computed(() => props.metrics.slaRisk)

const currentMonthLabel = computed(() => {
  const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre']
  const now = new Date()
  return MONTHS[now.getMonth()]
})

function formatType(hours) {
  if (hours === null || hours === undefined) return '—'
  return hours < 24 ? `${Math.round(hours)}h` : `${Math.round(hours / 24)}d`
}

function formatCurrency(n) {
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0, notation: 'compact' }).format(n)
}

function formatMinutes(minutes) {
  if (minutes < 0) return 'Vencido'
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return h > 0 ? `${h}h ${m}m` : `${m}m`
}
</script>

<style scoped>
.mnt-metrics {
  display: flex;
  align-items: stretch;
  gap: 1rem;
  padding: 0.75rem 1.5rem;
  height: auto;
  min-height: 80px;
  box-sizing: content-box;
  container-type: inline-size;
  container-name: mnt-metrics;
}

@container mnt-metrics (max-width: 900px) {
  .mnt-metrics .mnt-metric-card__sub--muted {
    display: none;
  }
}

@container mnt-metrics (max-width: 650px) {
  .mnt-metrics .mnt-metric-card {
    height: auto;
  }
  .mnt-metrics .mnt-metric-card__sub:nth-of-type(n+2) {
    display: none;
  }
}

.mnt-metric-card {
  flex: 1;
  min-width: 0;
  position: relative;
  background: var(--bg-surface-1);
  border: 1px solid var(--border-active);
  border-radius: 8px;
  border-top: 3px solid transparent;
  padding: 0.5rem 0.85rem;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
}

.mnt-metric-card--red { border-top-color: #e84545; }
.mnt-metric-card--amber { border-top-color: #f0a500; }
.mnt-metric-card--blue { border-top-color: #3b82f6; }
.mnt-metric-card--green { border-top-color: #10b981; }
.mnt-metric-card--gray { border-top-color: #94a3b8; }

.mnt-metric-card__label {
  font-family: var(--font-body);
  font-size: 11px;
  font-weight: 500;
  color: #64748b;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mnt-metric-card__value {
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 21px;
  color: #1e3a5f;
  line-height: 1.15;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mnt-metric-card--sla-card .mnt-metric-card__sub {
  font-size: 9.5px;
}

.mnt-metric-card__sublabel {
  font-family: var(--font-body);
  font-size: 10px;
  color: #94a3b8;
  margin-top: -0.15rem;
}

.mnt-metric-card__value--mono {
  font-family: var(--font-mono);
  font-size: 18px;
}

.mnt-metric-card__value--red { color: #e84545; }
.mnt-metric-card__value--gray { color: #94a3b8; }

.mnt-metric-card__sub {
  font-family: var(--font-body);
  font-size: 10.5px;
  color: var(--text-content);
  min-width: 0;
  width: 100%;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.mnt-metric-card__sub--muted { color: #94a3b8; font-size: 10px; }
.mnt-metric-card__sub--red { color: #dc2626; }
.mnt-metric-card__sub--red-bold { color: #dc2626; font-weight: 700; }
.mnt-metric-card__sub--amber { color: #d97706; font-size: 10px; }
.mnt-metric-card__sub--green { color: #16a34a; }
.mnt-metric-card__sub--navy { color: #1e3a5f; font-weight: 500; }

.mnt-metric-card__blink {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #e84545;
}

.mnt-metric-card__blink--normal {
  animation: mnt-blink 1.4s ease-in-out infinite;
}

.mnt-metric-card__blink--fast {
  animation: mnt-blink 0.8s ease-in-out infinite;
}

@keyframes mnt-blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.2; }
}
</style>